import { MessageCircle, Plus, Send, Sparkles, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent, type ReactElement } from "react";
import { Trans, useTranslation } from "react-i18next";

import { HelpFaqAnswer } from "@/components/layout/HelpFaqAnswer";
import { useHelp } from "@/components/layout/HelpProvider";
import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Input } from "@/components/ui/input";
import { textLinkClassName } from "@/components/ui/text-link";
import {
  HELP_FAQ_ITEMS,
  HELP_SUPPORT_EMAIL,
  HELP_SUGGESTED_FAQ_IDS,
  firstHelpName,
  helpItemQuestionKey,
} from "@/content/helpFaq";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

function HelpSuggestionCard({
  question,
  onSelect,
}: {
  question: string;
  onSelect: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex w-full items-start gap-3 rounded-xl border border-border bg-background px-3 py-3 text-left text-sm transition-colors hover:bg-accent"
    >
      <MessageCircle className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
      <span className="min-w-0 text-foreground">{question}</span>
    </button>
  );
}

export function HelpChatPanel({
  headerStart,
  showClose,
}: {
  headerStart?: ReactElement;
  showClose?: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { browseAll, asking, messages, closeChat, startNewChat, ask, openChat } = useHelp();
  const [draft, setDraft] = useState("");
  const threadEndRef = useRef<HTMLDivElement>(null);
  const name = firstHelpName(user?.displayName);
  const hasThread = messages.length > 0;
  const suggestionIds = browseAll
    ? HELP_FAQ_ITEMS.map((item) => item.id)
    : [...HELP_SUGGESTED_FAQ_IDS];

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ block: "end" });
  }, [asking, messages]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const question = draft.trim();
    if (question.length === 0 || asking) return;
    setDraft("");
    await ask(question);
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <div className="flex shrink-0 flex-row items-center gap-2 border-b border-border px-5 py-3.5">
        {headerStart}
        <Heading variant="drawer" as="h2" className="min-w-0 flex-1 truncate text-left">
          {hasThread ? t("help.chatTitle") : t("help.newChat")}
        </Heading>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          aria-label={t("help.newChat")}
          onClick={() => {
            startNewChat();
            setDraft("");
          }}
        >
          <Plus className="size-4" aria-hidden />
        </Button>
        {showClose ? (
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            aria-label={t("common.closeFlow")}
            onClick={() => {
              closeChat();
              setDraft("");
            }}
          >
            <X className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-y-contain px-5 py-5">
        {hasThread || asking ? (
          <div className="flex flex-col gap-3">
            {messages.map((message) => (
              <div
                key={message.id}
                className={cn(
                  "max-w-[92%] rounded-2xl px-3.5 py-2.5",
                  message.role === "user"
                    ? "self-end bg-primary text-primary-foreground"
                    : "self-start bg-muted",
                )}
              >
                {message.role === "user" ? (
                  <p className="text-sm">{message.text}</p>
                ) : (
                  <HelpFaqAnswer text={message.text} />
                )}
              </div>
            ))}
            {asking ? (
              <div className="self-start rounded-2xl bg-muted px-3.5 py-2.5 text-sm text-muted-foreground">
                {t("help.thinking")}
              </div>
            ) : null}
            <div ref={threadEndRef} />
          </div>
        ) : (
          <div className="flex flex-col gap-5">
            <div className="flex flex-col items-center gap-3 pt-4 text-center">
              <span className="flex size-12 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Sparkles className="size-5" aria-hidden />
              </span>
              <h3 className="text-lg font-medium text-balance">
                {name ? t("help.greeting", { name }) : t("help.greetingAnonymous")}
              </h3>
            </div>
            <p className="rounded-xl bg-muted px-3 py-2.5 text-center text-sm text-muted-foreground">
              {t("help.banner")}
            </p>
            <div className="flex flex-col gap-2">
              {suggestionIds.map((id) => (
                <HelpSuggestionCard
                  key={id}
                  question={t(helpItemQuestionKey(id))}
                  onSelect={() => openChat({ faqId: id })}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mt-auto flex shrink-0 flex-col items-stretch gap-2.5 border-t border-border p-5 max-lg:pb-[max(1.25rem,env(safe-area-inset-bottom))]">
        <form className="flex min-w-0 items-center gap-2" onSubmit={handleSubmit}>
          <Input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t("help.askPlaceholder")}
            aria-label={t("help.askPlaceholder")}
            className="w-auto min-w-0 flex-1 rounded-full"
            autoComplete="off"
            disabled={asking}
          />
          <Button
            type="submit"
            size="icon"
            className="size-9 shrink-0 rounded-full max-md:size-9"
            aria-label={t("help.send")}
            disabled={asking || draft.trim().length === 0}
          >
            <Send className="size-4" aria-hidden />
          </Button>
        </form>
        <p className="text-center text-xs text-muted-foreground">
          <Trans
            i18nKey="help.disclaimer"
            values={{ email: HELP_SUPPORT_EMAIL }}
            components={{
              email: <a href={`mailto:${HELP_SUPPORT_EMAIL}`} className={textLinkClassName} />,
            }}
          />
        </p>
      </div>
    </div>
  );
}
